import { QuickSearchLightbox } from '../../../common/quick-search-lightbox/QuickSearchLightbox';
import { defaultSearchItems } from './defaultSearchItems';
import { UniversalSearchFilters } from '../UniversalSearchFilters';
import { useObjectListUniversalSearch } from '../../object-list/useObjectListUniversalSearch';
import { useUniversalSearchContext } from '../UniversalSearchContext';
import { QuickSearchLightboxItem } from '../../../common/quick-search-lightbox/types';
import { useCallback, useMemo } from 'react';
import { sortSearchResults } from '../sortSearchResults';
import { getSearchListItems } from '../utils';
import { configurationTypeToIcon, DependenciesContentViewType } from '../../dependencies/types';
import { Connect } from '@sweep-io/sweep-design/dist/icons';
import { UniversalSearchListItem } from '../UniversalSearchListItem';
import { SearchListItem, SearchType } from '../types';
import pluralize from 'pluralize';

interface DocumentationLightboxProps {
  onClose: () => void;
  crmOrgId: string;
}

export const DocumentationLightbox = ({ onClose, crmOrgId }: DocumentationLightboxProps) => {
  const { filterOptions, selectedFilterValue } = useUniversalSearchContext();

  const {
    searchTxt,
    tempSearchTxt,
    onSearchKeydownWithEscHandler,
    onSearchChange,
    onClearButtonClick,
    onSelectedFilterItem,
    results,
    isLoadingResults,
    onListItemClick,
    searchTypeOptions,
    selectedSearchType,
    onSelectSearchTypeOption,
    handleCloseUniversalSearch,
    openFullModeUniversalSearch,
    performSearchProgrammatically,
  } = useObjectListUniversalSearch({
    crmOrgId,
  });

  const _onSelectSearchTypeOption = useCallback(
    async (selectedValue: string) => {
      await onSelectSearchTypeOption(selectedValue);

      // If switching to metadata search, navigate to full screen
      if (selectedValue === SearchType.content) {
        openFullModeUniversalSearch();
        onClose();
      }
    },
    [onSelectSearchTypeOption, openFullModeUniversalSearch, onClose],
  );

  const translatedResults: QuickSearchLightboxItem<SearchListItem>[] = useMemo(() => {
    if (!results) return [];

    const list = results
      ? getSearchListItems(sortSearchResults(results, crmOrgId, searchTxt))
      : undefined;

    return list
      ? list.map((item) => ({
          id: item.id,
          title: item.name,
          subtitle: item.label,
          icon: configurationTypeToIcon[item.parentType] ?? <Connect />,
          data: {
            ...item,
            contentType:
              item.match !== undefined
                ? DependenciesContentViewType.sourceCode
                : DependenciesContentViewType.default,
          },
        }))
      : [];
  }, [crmOrgId, results, searchTxt]);

  const onItemClick = useCallback(
    (item: QuickSearchLightboxItem<SearchListItem> | SearchListItem) => {
      // no data means that's default search item
      if (!('data' in item) && 'title' in item) {
        performSearchProgrammatically(item.title);
        return;
      }

      const searchListItem = 'data' in item;
      if (searchListItem) {
        openFullModeUniversalSearch();
        onListItemClick({
          ...item.data!,
          contentType:
            item.data!.match !== undefined
              ? DependenciesContentViewType.sourceCode
              : DependenciesContentViewType.default,
          clearHistory: true,
        });
        onClose();
      }
    },
    [onListItemClick, performSearchProgrammatically, onClose, openFullModeUniversalSearch],
  );

  const renderListElement = (item: SearchListItem) => {
    if (!item) return null;
    return (
      <UniversalSearchListItem
        item={item}
        onListItemClick={() =>
          onItemClick({
            id: item.id,
            title: item.name,
            subtitle: item.label,
            icon: configurationTypeToIcon[item.parentType] ?? <Connect />,
            data: item,
          })
        }
        isExpandedMode={false}
        forceRenderIcons={true}
        searchTxt={''}
      />
    );
  };

  return (
    <QuickSearchLightbox
      isOpen={true}
      displayShowAllButton={translatedResults?.length > 0}
      isLoading={isLoadingResults}
      renderListElement={renderListElement}
      onItemClick={onItemClick}
      onKeyDown={onSearchKeydownWithEscHandler}
      onShowAllClick={() => {
        openFullModeUniversalSearch();
        onClose();
      }}
      onClose={() => {
        handleCloseUniversalSearch();
        onClose();
      }}
      submittedInputValue={searchTxt}
      typedInputValue={tempSearchTxt}
      onClearSearchButtonClick={onClearButtonClick}
      items={searchTxt ? translatedResults : [...defaultSearchItems]}
      listTitle={
        searchTxt
          ? `Showing ${pluralize('result', translatedResults?.length ?? 0, true)}`
          : 'Suggested searches'
      }
      onSearchChange={onSearchChange}
      filterComponent={
        <UniversalSearchFilters
          filterOptions={filterOptions}
          selectedFilterValue={selectedFilterValue ?? ''}
          onSelectedFilterItem={onSelectedFilterItem}
          menuOptions={searchTypeOptions}
          selectedMenuOption={selectedSearchType}
          onSelectMenuOption={_onSelectSearchTypeOption}
        />
      }
    />
  );
};
